import { useState } from 'react';
import type { IPersonAccountLink } from '@skystone/shared';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { Dialog, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';

interface UnlinkConfirmDialogProps {
  open: boolean;
  link: IPersonAccountLink | null;
  personDisplayName: string;
  totalLinkCount: number;
  onClose: () => void;
  onConfirmed: () => void;
}

export function UnlinkConfirmDialog({
  open,
  link,
  personDisplayName,
  totalLinkCount,
  onClose,
  onConfirmed,
}: Readonly<UnlinkConfirmDialogProps>) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!link) {
    return <Dialog open={open} onClose={onClose} title="Unlink account"><p /></Dialog>;
  }

  const isLastLink = totalLinkCount <= 1;
  const isAppUserLink = link.source === 'app_user';

  const handleConfirm = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await personApi.unlink(link.id);
      onConfirmed();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to unlink'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Unlink account">
      <div className="space-y-3 text-sm">
        <p>
          Unlink <code>{link.source}</code> account{' '}
          <code>{link.sourceAccountId}</code> from{' '}
          <strong>{personDisplayName}</strong>? Auto-match will not re-link it.
        </p>

        {isAppUserLink && (
          <p className="rounded-md border border-warning-200 bg-warning-50 p-3 text-warning-700">
            <strong>Heads up:</strong> this is the app-login link for this Person. The Person
            will remain but will no longer be anchored to a skystone login account.
          </p>
        )}

        {isLastLink && !isAppUserLink && (
          <p className="rounded-md border border-warning-200 bg-warning-50 p-3 text-warning-700">
            <strong>Note:</strong> this is the last link on <strong>{personDisplayName}</strong>.
            They will remain as a floating Person with no linked accounts.
          </p>
        )}

        {error && <p className="text-sm text-error-700">{error}</p>}
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleConfirm} isLoading={submitting} disabled={submitting}>
          Unlink
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
